import type {
  ChallengeConfig, ChallengeEvent, Cuisine, DayKey, Meal, ScoringRules,
} from "./types.js";
import { condicoesLimpas, tipoDaMissao } from "./missoes.js";

/**
 * O que vale quando o dono do desafio não mexeu em nada.
 *
 * Todo campo de configuração tem padrão, e `resolveConfig` preenche os
 * buracos. É isso que permite um desafio criado hoje continuar funcionando
 * depois de a gente acrescentar um campo novo amanhã — sem migração.
 */
export const DEFAULT_RULES: ScoringRules = {
  bought: 1,
  homemade: 2,
  streakFrom: 7,
  streakMultiplier: 1.5,
  repeatMeal: 0,
  guessPrice: 0.5,
  guessKcal: 0.5,
  tolPrice: 0,
  tolKcal: 25,
  semFotoCusto: 0.5,
};

export const DEFAULT_MEALS: Meal[] = [
  { id: "cafe", label: "Café", emoji: "☕", weight: 1 },
  { id: "almoco", label: "Almoço", emoji: "🍽️", weight: 1 },
  { id: "lanche", label: "Lanche", emoji: "🥪", weight: 1 },
  { id: "janta", label: "Janta", emoji: "🍝", weight: 1 },
  { id: "sobremesa", label: "Sobremesa", emoji: "🍰", weight: 0.5 },
];

export const DEFAULT_CUISINES: Cuisine[] = [
  { id: "brasileira", label: "Brasileira", emoji: "🇧🇷" },
  { id: "churrasco", label: "Churrasco", emoji: "🥩" },
  { id: "mineira", label: "Mineira", emoji: "⛰️" },
  { id: "nordestina", label: "Nordestina", emoji: "🌵" },
  { id: "japonesa", label: "Japonesa", emoji: "🇯🇵" },
  { id: "chinesa", label: "Chinesa", emoji: "🇨🇳" },
  { id: "coreana", label: "Coreana", emoji: "🇰🇷" },
  { id: "tailandesa", label: "Tailandesa", emoji: "🇹🇭" },
  { id: "indiana", label: "Indiana", emoji: "🇮🇳" },
  { id: "arabe", label: "Árabe", emoji: "🧆" },
  { id: "italiana", label: "Italiana", emoji: "🇮🇹" },
  { id: "francesa", label: "Francesa", emoji: "🇫🇷" },
  { id: "portuguesa", label: "Portuguesa", emoji: "🇵🇹" },
  { id: "espanhola", label: "Espanhola", emoji: "🇪🇸" },
  { id: "grega", label: "Grega", emoji: "🇬🇷" },
  { id: "alema", label: "Alemã", emoji: "🇩🇪" },
  { id: "mexicana", label: "Mexicana", emoji: "🇲🇽" },
  { id: "peruana", label: "Peruana", emoji: "🇵🇪" },
  { id: "argentina", label: "Argentina", emoji: "🇦🇷" },
  { id: "americana", label: "Americana", emoji: "🍔" },
  { id: "vegetariana", label: "Vegetariana", emoji: "🥦" },
  { id: "doceria", label: "Doceria", emoji: "🍩" },
];

/** Marca gravada em cada prato dizendo por qual motor ele é pontuado. */
export const RULES_VERSION = 1;

export const DEFAULT_CONFIG: ChallengeConfig = {
  rules: DEFAULT_RULES,
  meals: DEFAULT_MEALS,
  cuisines: DEFAULT_CUISINES,
  events: [],
};

/** Completa uma configuração parcial com os padrões. Nunca devolve buraco. */
export function resolveConfig(partial?: Partial<ChallengeConfig> | null): ChallengeConfig {
  return {
    rules: { ...DEFAULT_RULES, ...(partial?.rules ?? {}) },
    meals: partial?.meals?.length ? partial.meals : DEFAULT_MEALS,
    cuisines: partial?.cuisines?.length ? partial.cuisines : DEFAULT_CUISINES,
    events: (partial?.events ?? []).map(eventoLimpo),
  };
}

/**
 * A missão como o motor pode confiar nela. A configuração é escrita pela
 * tela de regras e lida pelo servidor para dar ponto: condição malformada
 * (limite `NaN`, marca que a IA não conhece) viraria missão de graça ou
 * impossível, calada. Aqui ela some.
 */
function eventoLimpo(e: ChallengeEvent): ChallengeEvent {
  const tipo = tipoDaMissao(e);
  const { condicoes: _c, tipo: _t, refeicoes: _r, acao: _a, ...resto } = e;
  const base = { ...resto, cuisineIds: e.cuisineIds ?? [] };
  // Coleção: exatamente como sempre foi gravada.
  if (tipo === "colecao") return base;
  if (tipo === "atividade") {
    return { ...base, tipo, acao: e.acao === "comentar" ? "comentar" : "cravar" };
  }
  return {
    ...base, tipo, condicoes: condicoesLimpas(e.condicoes),
    ...(tipo === "diaCompleto"
      ? { refeicoes: (Array.isArray(e.refeicoes) ? e.refeicoes : []).filter((x) => typeof x === "string" && x) }
      : {}),
  };
}

export const findMeal = (config: ChallengeConfig, id: string | null): Meal | undefined =>
  id ? config.meals.find((m) => m.id === id) : undefined;

export const findCuisine = (config: ChallengeConfig, id: string | null): Cuisine | undefined =>
  id ? config.cuisines.find((c) => c.id === id) : undefined;

/**
 * Peso da refeição.
 * Refeição desconhecida — apagada da configuração, mas ainda presente em
 * pratos antigos — vale 1. Punir o passado por uma edição de hoje seria
 * mudar o placar de quem não fez nada.
 */
export function mealWeight(config: ChallengeConfig, mealId: string | null): number {
  if (!mealId) return 1;
  const w = findMeal(config, mealId)?.weight;
  return typeof w === "number" && isFinite(w) && w >= 0 ? w : 1;
}

/** O evento está valendo neste dia? */
export function eventOpen(event: ChallengeEvent, day: DayKey): boolean {
  if (event.from && day < event.from) return false;
  if (event.to && day > event.to) return false;
  return true;
}
